import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import { GET_MANAGED_CLIENTS } from "../../../../utils/GraphQL/queries";
import { UNLINK_CLIENT_MANAGER } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";

const fullName = (c) =>
  `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || "Unknown";

const ManagesListModal = ({
  managerId,
  managerName,
  onClose,
  onChildUnlinked,
}) => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();
  const [pendingId, setPendingId] = useState(null);

  const { data, loading, refetch } = useQuery(GET_MANAGED_CLIENTS, {
    variables: { managerId },
    fetchPolicy: "network-only",
  });
  const [unlinkMutation] = useMutation(UNLINK_CLIENT_MANAGER, {
    fetchPolicy: "network-only",
  });

  const children = data?.getManagedClients || [];

  const confirmRemove = async (child) => {
    const ok = await confirm({
      title: `Unlink ${fullName(child)}?`,
      message: `${fullName(child)} will no longer be managed by ${
        managerName || "this client"
      }.`,
      confirmLabel: "Unlink",
      destructive: true,
    });
    if (!ok) return;
    doRemove(child);
  };

  const doRemove = async (child) => {
    setPendingId(child?._id);
    try {
      await unlinkMutation({ variables: { clientId: child?._id } });
      await refetch();
      if (onChildUnlinked) await onChildUnlinked();
      handleSnack(`${fullName(child)} unlinked`, 2500);
    } catch (err) {
      handleSnack(`Unlink failed: ${err.message || err}`, 4000);
    } finally {
      setPendingId(null);
    }
  };

  const openInNewTab = (childId) => {
    if (!childId) return;
    window.open(
      `/bdr-office/clients/view-client/${childId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <BdrModal
      open
      onClose={onClose}
      title={`${managerName || "This client"} manages`}
      variant="panel"
      size="md"
      panelClassName="manages-list-modal"
    >
      <>
        {loading ? (
          <div className="manages-list-modal-loading">
            <CircularProgress size={28} sx={{ color: "#fff" }} />
          </div>
        ) : children.length === 0 ? (
          <div className="manages-list-modal-empty">No managed clients.</div>
        ) : (
          <ul className="manages-list-modal-list">
            {children.map((child) => {
              const isPending = pendingId === child?._id;
              return (
                <li
                  key={child?._id}
                  className="manages-list-modal-item"
                >
                  <span className="manages-list-modal-name">
                    {fullName(child)}
                  </span>
                  <span className="manages-list-modal-actions">
                    <button
                      type="button"
                      className="manager-link-icon-btn"
                      onClick={() => openInNewTab(child?._id)}
                      title="Open client in new tab"
                      aria-label="Open client in new tab"
                      disabled={isPending}
                    >
                      <OpenInNewIcon
                        sx={{ fontSize: 20, color: "#fff" }}
                      />
                    </button>
                    <button
                      type="button"
                      className="manager-link-icon-btn"
                      onClick={() => confirmRemove(child)}
                      title="Unlink from this manager"
                      aria-label="Unlink from this manager"
                      disabled={isPending}
                    >
                      {isPending ? (
                        <CircularProgress
                          size={18}
                          sx={{ color: "#fff" }}
                        />
                      ) : (
                        <LinkOffIcon
                          sx={{ fontSize: 20, color: "#fff" }}
                        />
                      )}
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </>
    </BdrModal>
  );
};

export default ManagesListModal;
